import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import axios from "axios";
import { useSelector } from "react-redux";

import PropertyImg4   from "../../assets/images/property-4.jpg";
import cityImg1       from "../../assets/images/city-1.jpg";
import cityImg2       from "../../assets/images/city-2.jpg";
import cityImg3       from "../../assets/images/city-3.jpg";
import cityImg4       from "../../assets/images/city-4.jpg";
import IconAbout1     from "../../assets/images/icon-about-1.svg";
import IconAbout2     from "../../assets/images/icon-about-2.svg";
import IconAbout3     from "../../assets/images/icon-about-3.svg";
import IconAbout4     from "../../assets/images/icon-about-4.svg";
import RightContImg   from "../../assets/images/intecontact.png";

import StatCounter    from "../../components/homepage/StatCounter";
import Notification, { showToast } from "../../components/common/notification/Notification";
import "./InteriorDesign.css";

const INTERIOR_TYPES = [
  { name: "Office Interior",     image: cityImg1, count: 22 },
  { name: "Home Interior",       image: cityImg2, count: 22 },
  { name: "Commercial Interior", image: cityImg3, count: 22 },
  { name: "Luxury Interior",     image: cityImg4, count: 22 },
];

const BENEFITS = [
  { icon: IconAbout1, text: "High quality interior design with low cost" },
  { icon: IconAbout2, text: "Dedicated interior architect aligned with customer needs" },
  { icon: IconAbout3, text: "Full support after completion of work" },
  { icon: IconAbout4, text: "Experienced and professional team" },
];

const initialValues = {
  propertyPurpose:   "",
  featuredProperty:  "",
  propertyType:      "",
  propertySubtype:   "",
  budget:            "",
  title:             "",
  country:           "India",
  state:             "",
  city:              "",
  area:              "",
  description:       "",
};

function InteriorDesign() {
  const formRef = useRef(null);
  const loggedInUser  = useSelector((state) => state.auth.user);
  const formData      = useSelector((state) => state.form.formData);
  const token         = loggedInUser?.token;
  const baseUrl       = process.env.REACT_APP_BASE_URL;

  const [propertyPurposes,     setPropertyPurposes]     = useState([]);
  const [propertyTypes,        setPropertyTypes]        = useState([]);
  const [propertySubtypes,     setPropertySubtypes]     = useState([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [selectedPropertyPurpose, setSelectedPropertyPurpose] = useState("");
  const [selectedState,        setSelectedState]        = useState(formData?.state || "");
  const [selectedCountry,      setSelectedCountry]      = useState("");
  const [states,               setStates]               = useState([]);
  const [cities,               setCities]               = useState([]);
  const [isCityDisabled,       setIsCityDisabled]       = useState(true);

  // Fetch purposes + types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const purposeRes = await axios.get(`${baseUrl}/all/property/purposes`);
        if (purposeRes.data.statusType === "SUCCESS")
          setPropertyPurposes(purposeRes.data.propertyPurposes);

        const typeRes = await axios.get(`${baseUrl}/all/property/types`);
        if (typeRes.data.statusType === "SUCCESS")
          setPropertyTypes(typeRes.data.propertyTypes);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Fetch subtypes when type changes
  useEffect(() => {
    const fetchSubtypes = async (propertyTypeId) => {
      if (!propertyTypeId) { setPropertySubtypes([]); return; }
      try {
        const res = await axios.get(`${baseUrl}/all/property/sub/types/${propertyTypeId}`);
        if (res.data.statusType === "SUCCESS")
          setPropertySubtypes(res.data.propertySubTypes);
        else setPropertySubtypes([]);
      } catch (error) {
        console.error("Error fetching property subtypes:", error);
        setPropertySubtypes([]);
      }
    };
    fetchSubtypes(selectedPropertyType);
  }, [selectedPropertyType, token]);

  // Fetch countries → states → cities
  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const countryRes = await axios.get(`${baseUrl}/all/countries`);
        if (countryRes.data.statusType === "SUCCESS") {
          const india = countryRes.data.countries.find((c) => c.name === "India");
          setSelectedCountry(india ? india.id : "");
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchGeo();
  }, []);

  useEffect(() => {
    const fetchStates = async (countryId) => {
      if (!countryId) { setStates([]); setIsCityDisabled(true); return; }
      try {
        const res = await axios.get(`${baseUrl}/all/states/${countryId}`);
        if (res.data.statusType === "SUCCESS") {
          setStates(res.data.states);
          setIsCityDisabled(false);
        } else setStates([]);
      } catch (error) {
        console.error("Error fetching states:", error);
        setStates([]);
        setIsCityDisabled(true);
      }
    };
    fetchStates(selectedCountry);
  }, [selectedCountry]);

  useEffect(() => {
    const fetchCities = async (stateId) => {
      if (!stateId) { setCities([]); return; }
      try {
        const res = await axios.get(`${baseUrl}/all/cities/${stateId}`);
        if (res.data.statusType === "SUCCESS") setCities(res.data.cities);
        else setCities([]);
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      }
    };
    fetchCities(selectedState);
  }, [selectedState]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const filterState = states.filter((s) => s.id === +values.state);
    const filterCity  = cities.filter((c) => c.id === +values.city);
    const data = {
      title:             "",
      description:       values.description,
      budget:            values.budget,
      purpose_id:        values.propertyPurpose,
      type_id:           +values.propertyType,
      sub_type_id:       +values.propertySubtype,
      featured_property: values.featuredProperty,
      city_name:         filterCity[0]?.name,
      state_name:        filterState[0]?.name,
      country_name:      "India",
      area_name:         values.area,
    };
    try {
      const response = await axios.post(`${baseUrl}/all/lead/add`, data);
      if (response.data.statusType === "SUCCESS") {
        showToast("Query added successfully", { type: "success" });
      } else {
        console.error("Error creating property:", response.text);
      }
    } catch (error) {
      showToast("Something went wrong", { type: "error" });
    }
    setSubmitting(false);
  };

  return (
    <div className="id-page">
      <Notification />

      {/* ── Hero ── */}
      <section className="id-page__hero">
        <div className="id-page__hero-bg" />
        <div className="id-page__hero-inner">
          <div className="id-page__container">
            <span className="id-page__hero-label">Interior Design</span>
            <h1 className="id-page__hero-title">
              Bringing <em>Life</em> to Interior
            </h1>
            <ol className="id-page__breadcrumb">
              <li><Link to="/">Home</Link></li>
              <li><span className="id-page__breadcrumb-sep">›</span></li>
              <li>Interior Design</li>
            </ol>
          </div>
        </div>
      </section>

      {/* ── Interior types grid ── */}
      <section className="id-page__section">
        <div className="id-page__container">
          <span className="id-page__section-label">Explore styles</span>
          <h2 className="id-page__section-title">
            Properties by <em>Interior</em>
          </h2>
          <div className="id-page__grid">
            {INTERIOR_TYPES.map(({ name, image, count }) => (
              <Link to="#" key={name} className="id-page__card">
                <img src={image} alt={name} className="id-page__card-img" />
                <div className="id-page__card-overlay" />
                <div className="id-page__card-body">
                  <span className="id-page__card-count">{count} Properties</span>
                  <h3 className="id-page__card-name">{name}</h3>
                  <span className="id-page__card-cta">
                    See More <ArrowRight size={12} strokeWidth={2.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="id-page__section id-page__section--alt">
        <div className="id-page__container">
          <div className="id-page__story">
            <img src={PropertyImg4} alt="Interior Design" className="id-page__story-img" />
            <div>
              <span className="id-page__section-label">Our Benefits</span>
              <h2 className="id-page__section-title">
                Fulfilling your desire in <em>design</em>
              </h2>
              <div className="id-page__benefits">
                {BENEFITS.map(({ icon, text }) => (
                  <div key={text} className="id-page__benefit">
                    <div className="id-page__benefit-icon">
                      <img src={icon} alt="" />
                    </div>
                    <p className="id-page__benefit-text">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stat counter ── */}
      <StatCounter />

      {/* ── Query form ── */}
      <section className="id-page__section" ref={formRef}>
        <div className="id-page__container">
          <div className="id-page__query">

            <div>
              <span className="id-page__section-label">Get in touch</span>
              <h2 className="id-page__section-title">
                Share your <em>query</em> with us
              </h2>

              <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="id-page__form">

                    {/* Purpose radio pills */}
                    <div>
                      <label className="id-page__field-label">I wish to</label>
                      <div className="id-page__purpose-row">
                        <FieldArray name="propertyPurpose">
                          {() => propertyPurposes.map((purpose) => (
                            <React.Fragment key={purpose.id}>
                              <input
                                className="id-page__purpose-radio"
                                type="radio"
                                id={`purpose-${purpose.id}`}
                                value={purpose.id}
                                checked={values.propertyPurpose === purpose.id}
                                onChange={() => {
                                  setFieldValue("propertyPurpose", purpose.id);
                                  setSelectedPropertyPurpose(purpose.id);
                                }}
                              />
                              <label className="id-page__purpose-label" htmlFor={`purpose-${purpose.id}`}>
                                {purpose.name}
                              </label>
                            </React.Fragment>
                          ))}
                        </FieldArray>
                      </div>
                      <ErrorMessage name="propertyPurpose" component="span" className="id-page__field-error" />
                    </div>

                    {/* Budget + Description */}
                    <div className="id-page__form-row">
                      <div>
                        <label className="id-page__field-label" htmlFor="budget">Budget</label>
                        <Field className="id-page__input" type="text" id="budget" name="budget" placeholder="Enter budget" />
                        <ErrorMessage name="budget" component="span" className="id-page__field-error" />
                      </div>
                      <div>
                        <label className="id-page__field-label" htmlFor="description">Description</label>
                        <Field className="id-page__input" type="text" id="description" name="description" placeholder="Enter description" />
                        <ErrorMessage name="description" component="span" className="id-page__field-error" />
                      </div>
                    </div>

                    {/* Property Type + Subtype */}
                    <div className="id-page__form-row">
                      <div>
                        <label className="id-page__field-label" htmlFor="propertyType">Property Type</label>
                        <Field
                          as="select"
                          className="id-page__select"
                          id="propertyType"
                          name="propertyType"
                          onChange={(e) => {
                            setFieldValue("propertyType", e.target.value);
                            setSelectedPropertyType(e.target.value);
                          }}
                        >
                          <option value="">Select property type</option>
                          {propertyTypes.map((type) => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="propertyType" component="span" className="id-page__field-error" />
                      </div>
                      <div>
                        <label className="id-page__field-label" htmlFor="propertySubtype">Property Subtype</label>
                        <Field
                          as="select"
                          className="id-page__select"
                          id="propertySubtype"
                          name="propertySubtype"
                          disabled={!selectedPropertyType}
                          onChange={(e) => setFieldValue("propertySubtype", e.target.value)}
                        >
                          <option value="">Select subtype</option>
                          {propertySubtypes.map((sub) => (
                            <option key={sub.id} value={sub.id}>{sub.name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="propertySubtype" component="span" className="id-page__field-error" />
                      </div>
                    </div>

                    {/* State + City */}
                    <div className="id-page__form-row">
                      <div>
                        <label className="id-page__field-label" htmlFor="state">State</label>
                        <Field
                          as="select"
                          className="id-page__select"
                          id="state"
                          name="state"
                          value={values.state}
                          onChange={(e) => {
                            setFieldValue("state", e.target.value);
                            setSelectedState(e.target.value);
                            setFieldValue("city", "");
                            setCities([]);
                          }}
                        >
                          <option value="">Select state</option>
                          {states.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="state" component="span" className="id-page__field-error" />
                      </div>
                      <div>
                        <label className="id-page__field-label" htmlFor="city">City</label>
                        <Field
                          as="select"
                          className="id-page__select"
                          id="city"
                          name="city"
                          disabled={isCityDisabled}
                          value={values.city}
                        >
                          <option value="">Select city</option>
                          {cities.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="city" component="span" className="id-page__field-error" />
                      </div>
                    </div>

                    {/* Area + Featured */}
                    <div className="id-page__form-row">
                      <div>
                        <label className="id-page__field-label" htmlFor="area">Area</label>
                        <Field className="id-page__input" type="text" id="area" name="area" placeholder="Enter area" />
                        <ErrorMessage name="area" component="span" className="id-page__field-error" />
                      </div>
                      <div>
                        <label className="id-page__field-label" htmlFor="featuredProperty">Featured Property</label>
                        <Field as="select" className="id-page__select" id="featuredProperty" name="featuredProperty">
                          <option value="">Select</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </Field>
                        <ErrorMessage name="featuredProperty" component="span" className="id-page__field-error" />
                      </div>
                    </div>

                    <button type="submit" className="id-page__submit-btn" disabled={isSubmitting}>
                      {isSubmitting ? "Submitting…" : "Submit Query"}
                    </button>

                  </Form>
                )}
              </Formik>
            </div>

            <img src={RightContImg} alt="Contact" className="id-page__query-img" />

          </div>
        </div>
      </section>
    </div>
  );
}

export default InteriorDesign;